import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useOrganization } from "./OrganizationContext";
import { listEnabledFeatureKeys } from "../utility/features";

const OrganizationFeaturesContext = createContext(null);
const EMPTY_KEYS = [];

export const useOrganizationFeatures = () => {
  const context = useContext(OrganizationFeaturesContext);
  if (!context) {
    return {
      ready: true,
      hasFeature: () => false,
    };
  }
  return context;
};

export const OrganizationFeaturesProvider = ({ children }) => {
  const { currentOrganization, loading: organizationLoading } = useOrganization();
  const organizationID = currentOrganization?.id ?? null;
  const [grantState, setGrantState] = useState({
    organizationID: null,
    keys: EMPTY_KEYS,
  });

  useEffect(() => {
    // No organization id yet is not a confirmed empty grant list. On a hard
    // refresh the org (and its roles) are still loading, so stay unresolved
    // until that fetch finishes and then until this org's grants arrive.
    if (organizationLoading || !organizationID) return undefined;

    let cancelled = false;
    listEnabledFeatureKeys(organizationID)
      .then((nextKeys) => {
        if (cancelled) return;
        setGrantState({
          organizationID,
          keys: nextKeys || EMPTY_KEYS,
        });
      })
      .catch((error) => {
        console.error(error);
        if (cancelled) return;
        setGrantState({ organizationID, keys: EMPTY_KEYS });
      });

    return () => {
      cancelled = true;
    };
  }, [organizationID, organizationLoading]);

  const grantsResolved =
    !organizationLoading &&
    (!organizationID || grantState.organizationID === organizationID);

  const keys =
    organizationID && grantState.organizationID === organizationID
      ? grantState.keys
      : EMPTY_KEYS;

  const value = useMemo(() => {
    const keySet = new Set(keys);
    return {
      ready: grantsResolved,
      hasFeature: (featureKey) => keySet.has(featureKey),
    };
  }, [keys, grantsResolved]);

  return (
    <OrganizationFeaturesContext.Provider value={value}>
      {children}
    </OrganizationFeaturesContext.Provider>
  );
};
